import { Handshake, Lock, LockOpen } from "lucide-react";
import type { ComponentType } from "react";
import { AVAILABILITY_MODES, type AvailabilityMode } from "@/utils/makersUtils";

interface ModeOption {
  mode: AvailabilityMode;
  /** Accessible label + tooltip describing what the mode does. */
  label: string;
  Icon: ComponentType<{ size?: number | string; "aria-hidden"?: boolean }>;
}

// Icon-only, in toggle order. The lock pair mirrors the prior checkbox ("include
// unconfirmed" = unlocked), and the handshake represents sold / Won work.
const MODE_OPTIONS: Record<AvailabilityMode, ModeOption> = {
  all: {
    mode: "all",
    label: "Include unconfirmed — count all confirmed and tentative work",
    Icon: LockOpen,
  },
  confirmed: {
    mode: "confirmed",
    label: "Confirmed only — exclude unconfirmed (tentative) work",
    Icon: Lock,
  },
  sold: {
    mode: "sold",
    label: "Sold work only — confirmed assignments on Won projects",
    Icon: Handshake,
  },
};

interface AvailabilityModeToggleProps {
  mode: AvailabilityMode;
  onChange: (mode: AvailabilityMode) => void;
  /** Icon size in px. Defaults to 16. */
  size?: number;
  className?: string;
}

/**
 * Three-way, icon-only segmented control for choosing how assignments count
 * toward availability: all work, confirmed-only, or sold (Won) work only.
 * Replaces the old "Include unconfirmed" checkbox on the Makers and
 * Availability boards.
 */
export function AvailabilityModeToggle({
  mode,
  onChange,
  size = 16,
  className,
}: AvailabilityModeToggleProps) {
  return (
    <div
      className={`availability-mode-toggle${className ? ` ${className}` : ""}`}
      role="radiogroup"
      aria-label="Availability mode"
    >
      {AVAILABILITY_MODES.map((value) => {
        const { label, Icon } = MODE_OPTIONS[value];
        const selected = value === mode;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={label}
            title={label}
            data-mode={value}
            className={`availability-mode-toggle__option${
              selected ? " availability-mode-toggle__option--selected" : ""
            }`}
            onClick={() => onChange(value)}
          >
            <Icon size={size} aria-hidden />
          </button>
        );
      })}
    </div>
  );
}
